import LumaResourceWorkspace, {lumaMoney} from '../../../components/admin/LumaResourceWorkspace';
import React, { useEffect, useState } from 'react';
import { fetchUsuarios, CustomUser } from '../../../services/userService';
import { fetchClientes } from '../../../services/clienteService';
import {
    Mail, Phone, CreditCard, Briefcase,
    Calendar, Search, MoreHorizontal, Building2, ArrowUpRight,
    CheckCircle2, ChevronRight, AlertCircle, FileCheck, Eye
} from 'lucide-react';
import { Link } from 'react-router-dom';
import ModalVerDeclaracionJurada from '../../../components/Clients/ModalVerDeclaracionJurada';

// ─── Avatar reutilizable ───────────────────────────────────────────────────────
interface UserAvatarProps {
    firstName?: string;
    lastName?: string;
    fotoUrl?: string | null;
}

const UserAvatar: React.FC<UserAvatarProps> = ({ firstName, lastName, fotoUrl }) => {
    const [imgError, setImgError] = useState(false);
    const showPhoto = fotoUrl && !imgError;

    return (
        <div className="relative">
            <div
                className="h-14 w-14 rounded-xl flex items-center justify-center text-xl font-bold text-white shadow-md transform group-hover:scale-105 transition-transform duration-300 overflow-hidden"
                style={{ backgroundColor: showPhoto ? 'transparent' : 'var(--ad-primary-dark, #8f1f64)' }}
            >
                {showPhoto ? (
                    <img
                        src={fotoUrl}
                        alt={`${firstName} ${lastName}`}
                        className="w-full h-full object-cover"
                        onError={() => setImgError(true)}
                    />
                ) : (
                    <span>{firstName?.[0]}{lastName?.[0]}</span>
                )}
            </div>
            {/* Indicador de estado (se posiciona sobre el avatar) */}
        </div>
    );
};
// ──────────────────────────────────────────────────────────────────────────────

const UsuariosPage = () => {
    const [usuarios, setUsuarios] = useState<CustomUser[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');

    // Mapa userId → foto_url
    const [fotoMap, setFotoMap] = useState<Record<number, string | null>>({});
    // Mapa userId → info declaracion jurada
    const [declaracionMap, setDeclaracionMap] = useState<Record<number, { firmada: boolean; fecha?: string | null; foto_url?: string | null }>>({});
    const [filtroDeclaracion, setFiltroDeclaracion] = useState<'todos' | 'firmada' | 'pendiente'>('todos');
    const [modalDeclaracionOpen, setModalDeclaracionOpen] = useState(false);
    const [clienteDeclaracionModal, setClienteDeclaracionModal] = useState<{
        nombre?: string;
        email?: string;
        telefono?: string | null;
        ci_nit?: string | null;
        tipo_cliente?: string;
        fecha_declaracion_jurada?: string | null;
        foto_declaracion_jurada_url?: string | null;
    } | null>(null);

    // --- PALETA DE COLORES COMARCA (Formal: 3 colores clave) ---
    const theme = {
        primaryAction: 'var(--ad-primary, #3564db)',
        corporate: 'var(--ad-primary-dark, #8f1f64)',
        blue: 'var(--ad-secondary, #3564db)',
    };

    useEffect(() => {
        const loadData = async () => {
            setIsLoading(true);
            try {
                // Carga en paralelo: usuarios + clientes + personal
                const [resultado, clientes] = await Promise.all([
                    fetchUsuarios('cliente'),
                    fetchClientes(),
                ]);

                setUsuarios(resultado.filter(u=>(u.tipo_nivel_usuario?.perfil_asociado || u.perfil_asociado)==='cliente'));

                // Construir mapa userId → foto_url y declaracion jurada
                const fMap: Record<number, string | null> = {};
                const dMap: Record<number, { firmada: boolean; fecha?: string | null; foto_url?: string | null }> = {};

                clientes.forEach((c) => {
                    if (c.usuario?.id != null) {
                        fMap[c.usuario.id] = c.foto_url ?? null;
                        dMap[c.usuario.id] = {
                            firmada: Boolean(c.declaracion_jurada_firmada),
                            fecha: c.fecha_declaracion_jurada,
                            foto_url: c.foto_declaracion_jurada_url
                        };
                    }
                });
                setFotoMap(fMap);
                setDeclaracionMap(dMap);
            } catch (error) {
                /* Browser diagnostics omitted. */
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    const filteredUsuarios = usuarios.filter(usuario => {
        const fullName = `${usuario.first_name || ''} ${usuario.last_name || ''}`.toLowerCase();
        const email = usuario.email?.toLowerCase() || '';
        const phone = usuario.telefono?.toLowerCase() || '';
        const ciNit = usuario.ci_nit?.toLowerCase() || '';
        const cargo = usuario.cargo?.toLowerCase() || '';
        const term = searchTerm.toLowerCase();
        const matchSearch = fullName.includes(term) || email.includes(term) || phone.includes(term) || ciNit.includes(term) || cargo.includes(term);

        if (!matchSearch) return false;

        const decInfo = declaracionMap[usuario.id];
        if (filtroDeclaracion === 'firmada') {
            return decInfo?.firmada === true;
        }
        if (filtroDeclaracion === 'pendiente') {
            return usuario.perfil_asociado === 'cliente' && !decInfo?.firmada;
        }

        return true;
    }).sort((a, b) => b.id - a.id);

    const handleVerDeclaracion = (usuario: CustomUser) => {
        const decInfo = declaracionMap[usuario.id];
        setClienteDeclaracionModal({
            nombre: `${usuario.first_name || ''} ${usuario.last_name || ''}`.trim() || usuario.email,
            email: usuario.email,
            telefono: usuario.telefono,
            ci_nit: usuario.ci_nit,
            tipo_cliente: usuario.tipo_nivel_usuario?.nombre || 'Cliente',
            fecha_declaracion_jurada: decInfo?.fecha,
            foto_declaracion_jurada_url: decInfo?.foto_url,
        });
        setModalDeclaracionOpen(true);
    };

    const getTipoUsuario = (tipoNivel: { id: number; nombre: string } | null) => {
        return tipoNivel?.nombre || 'Miembro';
    };

    const getBadgeStyles = (tipoNivel: { id: number; nombre: string } | null) => {
        const base = "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-semibold tracking-wide border";
        if (!tipoNivel) return `${base} bg-gray-50 text-gray-600 border-gray-200`;

        switch (tipoNivel.id) {
            case 1: return `${base} bg-[#8f1f64]/5 text-[#8f1f64] border-[#8f1f64]/20`;
            case 2: return `${base} bg-[#3564db]/5 text-[#3564db] border-[#3564db]/20`;
            case 3: return `${base} bg-[#ffd97c]/20 text-[#ea8406] border-[#ffd97c]/50`;
            case 4: return `${base} bg-[#3564db]/20 text-[#3564db] border-[#ffd97c]/50`;
            case 5: return `${base} bg-[#ea8406]/20 text-[#ea8406] border-[#ffd97c]/50`;
            case 6: return `${base} bg-[#3564db]/20 text-[#3564db] border-[#92b1ed]/50`;
            case 7: return `${base} bg-[#ea8406]/20 text-[#ea8406] border-[#92b1ed]/50`;
            default: return `${base} bg-[#ea8406]/5 text-[#ea8406] border-[#ffd97c]/20`;
        }
    };

    const formatDate = (dateString?: string) => {
        if (!dateString) return 'No disponible';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return 'Fecha inválida';
            return date.toLocaleDateString('es-ES', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch {
            return 'Fecha inválida';
        }
    };

    return <><LumaResourceWorkspace title="Kardex de citas" description="Consulta y gestiona la información de tu coworking." group="Kardex" rows={filteredUsuarios.map(r=>({...r,nombre_completo:`${r.first_name} ${r.last_name}`}))} columns={[{key:'nombre_completo',label:'Cliente'},{key:'email',label:'Email'},{key:'telefono',label:'Teléfono'},{key:'ci_nit',label:'CI / NIT'},{key:'tipo_nivel_usuario',label:'Nivel',render:r=>r.tipo_nivel_usuario?.nombre||'Sin nivel'}]} idKey="id" nameKey="nombre_completo" statusKey="estado" canRead={true} onCreate={undefined} onEdit={undefined} onDelete={undefined} extraActions={r=><><Link to={`/kardex-citas/detalle/${r.id}`}>Abrir kardex ↗</Link><button type="button" onClick={()=>handleVerDeclaracion(r)}>Declaración jurada</button></>}><div className="luma-tabs">{(['todos','firmada','pendiente'] as const).map(value=><button type="button" key={value} aria-pressed={filtroDeclaracion===value} onClick={()=>setFiltroDeclaracion(value)}>{value==='todos'?'Todos':value==='firmada'?'Declaración firmada':'Declaración pendiente'}</button>)}</div></LumaResourceWorkspace><ModalVerDeclaracionJurada
                    isOpen={modalDeclaracionOpen}
                    onClose={() => setModalDeclaracionOpen(false)}
                    cliente={clienteDeclaracionModal}
                /></>;
};

export default UsuariosPage;
